import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Assessment,
  AttachMoney,
  People,
  Timeline,
  BarChart,
  PieChart,
  ShowChart
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart as RechartsBarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { getMonthlySummaryData, generateMonthlySummaryData } from '../../../services/request/Reports';
import {
  MonthlySummaryResponse,
  MonthlyReport,
  ChartDataPoint
} from '../../../models/ReportInterface';

const ReportsDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<MonthlySummaryResponse | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'6' | '12' | '24'>('12');
  const [lastGenerated, setLastGenerated] = useState<Date | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadReportData();
  }, [selectedPeriod]);

  const loadReportData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getMonthlySummaryData();
      setReportData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    try {
      setGenerating(true);
      setError(null);
      setSuccessMessage(null);
      
      const data = await generateMonthlySummaryData();
      setReportData(data);
      setLastGenerated(new Date());
      setSuccessMessage('Monthly summary report generated successfully!');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'INACTIVE': return 'error';
      case 'PENDING': return 'warning';
      default: return 'default';
    }
  };

  const getTrendIcon = (current: number, previous: number) => {
    if (current > previous) return <TrendingUp color="success" />;
    if (current < previous) return <TrendingDown color="error" />;
    return <Timeline color="info" />;
  };

  const getTrendColor = (current: number, previous: number) => {
    if (current > previous) return 'success.main';
    if (current < previous) return 'error.main';
    return 'info.main';
  };

  const calculateTrend = (current: number, previous: number) => {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  // Prepare chart data
  const prepareChartData = () => {
    if (!reportData?.reports || !Array.isArray(reportData.reports)) return [];
    
    const sortedReports = [...reportData.reports].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });

    return sortedReports.slice(-parseInt(selectedPeriod)).map(report => ({
      month: report.monthName,
      outstanding: report.totalOutstanding,
      collections: report.totalCollections,
      interest: report.interestCollected,
      borrowers: report.totalBorrowers,
      efficiency: report.collectionEfficiency
    }));
  };

  const chartData = prepareChartData();

  // Prepare pie chart data for outstanding vs collections
  const pieChartData = reportData ? [
    { name: 'Total Outstanding', value: reportData.totalOutstanding, color: '#8884d8' },
    { name: 'Total Collections', value: reportData.totalCollections, color: '#82ca9d' }
  ] : [];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={loadReportData} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="info">
        No report data available
      </Alert>
    );
  }

  const latestReport = reportData.reports && reportData.reports.length > 0 
    ? reportData.reports[reportData.reports.length - 1] 
    : null;
  const previousReport = reportData.reports && reportData.reports.length > 1 
    ? reportData.reports[reportData.reports.length - 2] 
    : null;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Reports Dashboard
      </Typography>

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Period Selection */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Report Period</InputLabel>
              <Select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value as '6' | '12' | '24')}
              >
                <MenuItem value="6">Last 6 Months</MenuItem>
                <MenuItem value="12">Last 12 Months</MenuItem>
                <MenuItem value="24">Last 24 Months</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              onClick={loadReportData}
              fullWidth
              startIcon={<Assessment />}
              disabled={loading}
            >
              Refresh Reports
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              color="primary"
              onClick={generateReport}
              fullWidth
              startIcon={generating ? <CircularProgress size={20} /> : <Timeline />}
              disabled={generating || loading}
              sx={{ 
                border: '2px solid',
                '&:hover': {
                  border: '2px solid',
                  backgroundColor: 'primary.main',
                  color: 'white'
                }
              }}
            >
              {generating ? 'Generating...' : 'Generate Report'}
            </Button>
          </Grid>
        </Grid>
        {lastGenerated && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary">
              Last generated: {lastGenerated.toLocaleString()}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AttachMoney color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Total Outstanding
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(reportData.totalOutstanding)}
                  </Typography>
                  {previousReport && (
                    <Box display="flex" alignItems="center" mt={1}>
                      {getTrendIcon(reportData.totalOutstanding, previousReport.totalOutstanding)}
                      <Typography 
                        variant="caption" 
                        color={getTrendColor(reportData.totalOutstanding, previousReport.totalOutstanding)}
                        sx={{ ml: 1 }}
                      >
                        {formatPercentage(calculateTrend(reportData.totalOutstanding, previousReport.totalOutstanding))}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Total Collections
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(reportData.totalCollections)}
                  </Typography>
                  {previousReport && (
                    <Box display="flex" alignItems="center" mt={1}>
                      {getTrendIcon(reportData.totalCollections, previousReport.totalCollections)}
                      <Typography 
                        variant="caption" 
                        color={getTrendColor(reportData.totalCollections, previousReport.totalCollections)}
                        sx={{ ml: 1 }}
                      >
                        {formatPercentage(calculateTrend(reportData.totalCollections, previousReport.totalCollections))}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Assessment color="info" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Interest Collected
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(reportData.totalInterestCollected)}
                  </Typography>
                  {previousReport && (
                    <Box display="flex" alignItems="center" mt={1}>
                      {getTrendIcon(reportData.totalInterestCollected, previousReport.interestCollected)}
                      <Typography 
                        variant="caption" 
                        color={getTrendColor(reportData.totalInterestCollected, previousReport.interestCollected)}
                        sx={{ ml: 1 }}
                      >
                        {formatPercentage(calculateTrend(reportData.totalInterestCollected, previousReport.interestCollected))}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <People color="warning" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Total Reports
                  </Typography>
                  <Typography variant="h6">
                    {reportData.totalReports}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Latest: {latestReport?.monthName} {latestReport?.year}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Outstanding vs Collections Line Chart */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Outstanding vs Collections Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="outstanding" 
                    stroke="#8884d8" 
                    strokeWidth={2}
                    name="Outstanding"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="collections" 
                    stroke="#82ca9d" 
                    strokeWidth={2}
                    name="Collections"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Outstanding vs Collections Pie Chart */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Outstanding vs Collections
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                </RechartsPieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Collection Efficiency and Borrowers Chart */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Collection Efficiency Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                  <Area 
                    type="monotone" 
                    dataKey="efficiency" 
                    stroke="#8884d8" 
                    fill="#8884d8"
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Borrowers Count Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsBarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="borrowers" fill="#82ca9d" />
                </RechartsBarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Monthly Reports Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Monthly Reports Summary
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                  <TableCell align="right">Collections</TableCell>
                  <TableCell align="right">Borrowers</TableCell>
                  <TableCell align="right">Efficiency</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.reports && reportData.reports.length > 0 ? (
                  reportData.reports.slice(-parseInt(selectedPeriod)).reverse().map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>
                        {report.monthName} {report.year}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(report.totalOutstanding)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(report.totalCollections)}
                      </TableCell>
                      <TableCell align="right">
                        {report.totalBorrowers}
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center">
                          <LinearProgress
                            variant="determinate"
                            value={report.collectionEfficiency}
                            sx={{ width: 60, mr: 1 }}
                          />
                          {formatPercentage(report.collectionEfficiency)}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={report.status}
                          color={getStatusColor(report.status) as any}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary">
                        No reports available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReportsDashboard;
